import { create } from 'zustand';
import {
  SortingState,
  ColumnFiltersState,
  PaginationState,
} from '@tanstack/react-table';

// ============================================================================
// Export Helper Functions
// ============================================================================

/**
 * Escape a CSV field (add quotes if needed)
 */
function escapeCSVField(field: string): string {
  if (
    field.includes(',') ||
    field.includes('"') ||
    field.includes('\n') ||
    field.includes('\r')
  ) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}

/**
 * Format a value for CSV (handle null, undefined, objects)
 */
function formatCSVValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'boolean') {
    return value ? 'TRUE' : 'FALSE';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

/**
 * Convert JSON data to CSV format
 * Excel-friendly with proper quoting and special character handling
 *
 * @param data Array of records to convert
 * @param columnOrder Optional array of column names in desired order
 * @returns CSV string
 */
function convertToCSV(
  data: Record<string, unknown>[],
  columnOrder?: string[]
): string {
  if (data.length === 0) {
    return '';
  }

  // Get all unique keys across all records
  let keys: string[];

  if (columnOrder && columnOrder.length > 0) {
    const dataKeys = new Set(data.flatMap((record) => Object.keys(record)));
    keys = columnOrder.filter((key) => dataKeys.has(key));

    // Add any keys not in columnOrder
    const orderedSet = new Set(keys);
    for (const key of dataKeys) {
      if (!orderedSet.has(key)) {
        keys.push(key);
      }
    }
  } else {
    keys = Array.from(new Set(data.flatMap((record) => Object.keys(record))));
  }

  // Create CSV header
  const header = keys.map((key) => escapeCSVField(String(key))).join(',');

  // Create CSV rows
  const rows = data.map((record) => {
    return keys
      .map((key) => {
        const value = record[key];
        return escapeCSVField(formatCSVValue(value));
      })
      .join(',');
  });

  return [header, ...rows].join('\n');
}

/**
 * Convert JSON data to XLSX (Excel) format
 * Creates a single-sheet workbook with the data
 * Uses dynamic import to load xlsx only when needed (code splitting)
 *
 * @param data Array of records to convert
 * @param sheetName Name for the Excel sheet
 * @param columnOrder Optional array of column names in desired order
 * @returns Buffer containing XLSX data
 */
async function convertToXLSX(
  data: Record<string, unknown>[],
  sheetName: string = 'Data',
  columnOrder?: string[]
): Promise<ArrayBuffer> {
  // Dynamic import - xlsx is only loaded when actually exporting to Excel
  const XLSX = await import('xlsx');

  // Sanitize sheet name (max 31 chars, no special chars)
  const safeSheetName = sheetName
    .replace(/[\\/?*[\]]/g, '')
    .trim()
    .substring(0, 31) || 'Data';

  const workbook = XLSX.utils.book_new();

  if (data.length === 0) {
    const emptyWorksheet = XLSX.utils.aoa_to_sheet([[]]);
    XLSX.utils.book_append_sheet(workbook, emptyWorksheet, safeSheetName);
  } else {
    // Get all unique keys
    let keys: string[];

    if (columnOrder && columnOrder.length > 0) {
      const dataKeys = new Set(data.flatMap((record) => Object.keys(record)));
      keys = columnOrder.filter((key) => dataKeys.has(key));

      const orderedSet = new Set(keys);
      for (const key of dataKeys) {
        if (!orderedSet.has(key)) {
          keys.push(key);
        }
      }
    } else {
      keys = Array.from(
        new Set(data.flatMap((record) => Object.keys(record)))
      ).sort((a, b) => a.localeCompare(b));
    }

    // ISO date regex for detecting date strings
    const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}/;

    // Helper to coerce values for Excel cells
    // Converts ISO date strings to Date objects for native Excel dates
    const toCell = (value: unknown): unknown => {
      if (value === null || value === undefined) return '';
      if (typeof value === 'boolean') return value;
      if (typeof value === 'object') return JSON.stringify(value);
      // Convert ISO date/datetime strings to Date objects for native Excel dates
      if (typeof value === 'string' && value.length >= 10 && ISO_DATE_REGEX.test(value)) {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
      return value;
    };

    // Prepare rows with stable column order
    const excelData = data.map((record) => {
      const row: Record<string, unknown> = {};
      for (const key of keys) {
        row[key] = toCell(record[key]);
      }
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName);
  }

  // Generate Excel buffer
  const buffer = XLSX.write(workbook, {
    type: 'array',
    bookType: 'xlsx',
    compression: true,
    cellDates: true, // Preserve Date objects as native Excel dates
  });

  return buffer as ArrayBuffer;
}

// ============================================================================
// Table State Management
// ============================================================================

/**
 * Table state management store
 *
 * This store provides comprehensive state management for data tables
 * using TanStack Table. It handles sorting, filtering, pagination,
 * column visibility, row selection, and other table configurations.
 *
 * Key features:
 * - Multiple table support with unique configurations
 * - Persistent table state across sessions
 * - Sorting and filtering management
 * - Column visibility control
 * - Row selection handling
 * - Pagination state management
 * - Export functionality for CSV, JSON, and Excel
 */

/**
 * Table configuration interface
 *
 * Defines the complete configuration state for a single table,
 * including all TanStack Table state properties.
 */
export interface TableConfig {
  /** Current sorting configuration */
  sorting: SortingState;
  /** Current column filters */
  columnFilters: ColumnFiltersState;
  /** Column visibility state */
  columnVisibility: Record<string, boolean>;
  /** Row selection state */
  rowSelection: Record<string, boolean>;
  /** Global search filter */
  globalFilter: string;
  /** Pagination state */
  pagination: PaginationState;
}

/**
 * Table state management interface
 *
 * Defines the complete table store that manages multiple table
 * configurations and provides comprehensive table state management
 * actions for sorting, filtering, pagination, and more.
 */
interface TableStore {
  // ===== TABLE STATE =====
  /** Map of table IDs to their configurations */
  tableConfigs: Record<string, TableConfig>;
  /** Currently active table ID */
  activeTableId: string | null;

  // ===== GENERAL ACTIONS =====
  /** Set the active table */
  setActiveTable: (tableId: string) => void;
  /** Update table configuration */
  updateTableConfig: (tableId: string, config: Partial<TableConfig>) => void;
  /** Reset table configuration to defaults */
  resetTableConfig: (tableId: string) => void;
  /** Clear all table configurations */
  clearAllTableConfigs: () => void;

  // ===== SORTING ACTIONS =====
  /** Set sorting configuration for a table */
  setSorting: (tableId: string, sorting: SortingState) => void;

  // ===== FILTERING ACTIONS =====
  /** Set column filters for a table */
  setColumnFilters: (tableId: string, filters: ColumnFiltersState) => void;
  /** Set global search filter for a table */
  setGlobalFilter: (tableId: string, filter: string) => void;

  // ===== COLUMN VISIBILITY ACTIONS =====
  /** Set column visibility for a table */
  setColumnVisibility: (
    tableId: string,
    visibility: Record<string, boolean>
  ) => void;
  /** Toggle visibility of a specific column */
  toggleColumnVisibility: (tableId: string, columnId: string) => void;

  // ===== ROW SELECTION ACTIONS =====
  /** Set row selection for a table */
  setRowSelection: (
    tableId: string,
    selection: Record<string, boolean>
  ) => void;
  /** Toggle selection of a specific row */
  toggleRowSelection: (tableId: string, rowId: string) => void;
  /** Select all rows in a table */
  selectAllRows: (tableId: string, rowIds: string[]) => void;
  /** Clear all row selections */
  clearRowSelection: (tableId: string) => void;

  // ===== PAGINATION ACTIONS =====
  /** Set pagination configuration for a table */
  setPagination: (tableId: string, pagination: PaginationState) => void;
  /** Set page size for a table */
  setPageSize: (tableId: string, pageSize: number) => void;
  /** Set page index for a table */
  setPageIndex: (tableId: string, pageIndex: number) => void;

  // ===== UTILITY ACTIONS =====
  /** Get table configuration (returns default if not found) */
  getTableConfig: (tableId: string) => TableConfig;
  /** Export table data to CSV, JSON, or Excel format */
  exportTableData: (
    tableId: string,
    format: 'csv' | 'json' | 'excel',
    data: Record<string, unknown>[],
    columns?: Array<{ id: string; header: string }>
  ) => Promise<void>;
}

/**
 * Default table configuration
 *
 * Provides sensible defaults for new table configurations.
 * Used when creating new table configs or resetting existing ones.
 */
const defaultTableConfig: TableConfig = {
  sorting: [],
  columnFilters: [],
  columnVisibility: {},
  rowSelection: {},
  globalFilter: '',
  pagination: {
    pageIndex: 0,
    pageSize: 10,
  },
};

/**
 * Table store implementation
 *
 * Creates a Zustand store for managing table configurations.
 * The store supports multiple tables with independent configurations
 * and provides fresh state on every page load.
 *
 * Key features:
 * - Multiple table support
 * - Comprehensive table state management
 * - Type-safe operations
 * - Export functionality (planned)
 * - Fresh state on every page load (no persistence)
 */
export const useTableStore = create<TableStore>()((set, get) => ({
  // ===== TABLE STATE =====
  /** Map of table configurations */
  tableConfigs: {},
  /** Currently active table ID */
  activeTableId: null,

  // ===== GENERAL ACTIONS =====

  /** Set the active table */
  setActiveTable: (activeTableId) => set({ activeTableId }),

  /**
   * Update table configuration
   *
   * Merges new configuration with existing config or creates new one
   * with defaults if table doesn't exist.
   *
   * @param tableId Unique table identifier
   * @param config Partial configuration to merge
   */
  updateTableConfig: (tableId, config) => {
    set((state) => ({
      tableConfigs: {
        ...state.tableConfigs,
        [tableId]: {
          ...(state.tableConfigs[tableId] || defaultTableConfig),
          ...config,
        },
      },
    }));
  },

  /**
   * Reset table configuration to defaults
   *
   * @param tableId Unique table identifier
   */
  resetTableConfig: (tableId) => {
    set((state) => ({
      tableConfigs: {
        ...state.tableConfigs,
        [tableId]: defaultTableConfig,
      },
    }));
  },

  /** Clear all table configurations and reset active table */
  clearAllTableConfigs: () => set({ tableConfigs: {}, activeTableId: null }),

  // Sorting actions
  setSorting: (tableId, sorting) => {
    get().updateTableConfig(tableId, { sorting });
  },

  // Filtering actions
  setColumnFilters: (tableId, columnFilters) => {
    get().updateTableConfig(tableId, { columnFilters });
  },

  setGlobalFilter: (tableId, globalFilter) => {
    get().updateTableConfig(tableId, { globalFilter });
  },

  // Column visibility actions
  setColumnVisibility: (tableId, columnVisibility) => {
    get().updateTableConfig(tableId, { columnVisibility });
  },

  toggleColumnVisibility: (tableId, columnId) => {
    const currentConfig = get().getTableConfig(tableId);
    const newVisibility = {
      ...currentConfig.columnVisibility,
      [columnId]: !currentConfig.columnVisibility[columnId],
    };
    get().setColumnVisibility(tableId, newVisibility);
  },

  // Row selection actions
  setRowSelection: (tableId, rowSelection) => {
    get().updateTableConfig(tableId, { rowSelection });
  },

  toggleRowSelection: (tableId, rowId) => {
    const currentConfig = get().getTableConfig(tableId);
    const newSelection = {
      ...currentConfig.rowSelection,
      [rowId]: !currentConfig.rowSelection[rowId],
    };
    get().setRowSelection(tableId, newSelection);
  },

  selectAllRows: (tableId, rowIds) => {
    const selection = rowIds.reduce(
      (acc, rowId) => {
        acc[rowId] = true;
        return acc;
      },
      {} as Record<string, boolean>
    );
    get().setRowSelection(tableId, selection);
  },

  clearRowSelection: (tableId) => {
    get().setRowSelection(tableId, {});
  },

  // Pagination actions
  setPagination: (tableId, pagination) => {
    get().updateTableConfig(tableId, { pagination });
  },

  setPageSize: (tableId, pageSize) => {
    const currentConfig = get().getTableConfig(tableId);
    get().setPagination(tableId, {
      ...currentConfig.pagination,
      pageSize,
      pageIndex: 0, // Reset to first page when changing page size
    });
  },

  setPageIndex: (tableId, pageIndex) => {
    const currentConfig = get().getTableConfig(tableId);
    get().setPagination(tableId, {
      ...currentConfig.pagination,
      pageIndex,
    });
  },

  // ===== UTILITY ACTIONS =====

  /**
   * Get table configuration
   *
   * Returns the configuration for a table or default config if not found.
   *
   * @param tableId Unique table identifier
   * @returns Table configuration
   */
  getTableConfig: (tableId) => {
    const { tableConfigs } = get();
    return tableConfigs[tableId] || defaultTableConfig;
  },

  /**
   * Export table data
   *
   * Exports the current table data in the specified format (CSV, JSON, or Excel).
   * This function needs to be called with the actual data since the store doesn't
   * hold the data itself - only the UI state.
   *
   * @param tableId Unique table identifier (used for generating filename)
   * @param format Export format ('csv', 'json', or 'excel')
   * @param data Array of records to export
   * @param columns Optional array of column definitions for field ordering
   */
  exportTableData: async (
    tableId: string,
    format: 'csv' | 'json' | 'excel',
    data: Record<string, unknown>[],
    columns?: Array<{ id: string; header: string }>
  ) => {
    if (!data || data.length === 0) {
      console.warn('No data to export');
      return;
    }

    try {
      let blob: Blob;
      let filename: string;
      const timestamp = new Date().toISOString().split('T')[0];

      // Extract column order from columns prop if provided
      const columnOrder = columns?.map((col) => col.id);

      switch (format) {
        case 'csv': {
          const csv = convertToCSV(data, columnOrder);
          blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
          filename = `${tableId}-${timestamp}.csv`;
          break;
        }

        case 'json': {
          const json = JSON.stringify(data, null, 2);
          blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
          filename = `${tableId}-${timestamp}.json`;
          break;
        }

        case 'excel': {
          const excelBuffer = await convertToXLSX(data, tableId, columnOrder);
          blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          filename = `${tableId}-${timestamp}.xlsx`;
          break;
        }

        default:
          console.error(`Unsupported format: ${format}`);
          return;
      }

      // Trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      throw error;
    }
  },
}));
